import type { ReactNode } from 'react';

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

interface DrawerProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}

/**
 * The record drawer every list screen opens.
 *
 * Callers mount it conditionally (`{id === undefined ? null : <Drawer …>}`),
 * so it is always open when it exists and `onOpenChange` only ever fires to
 * close — which is what keeps the call sites unchanged from the hand-rolled
 * version this replaces. What is new is everything Radix brings: a focus
 * trap, scroll lock, and a portal, so the drawer is no longer clipped by
 * whatever container the screen happened to render it inside.
 */
export function Drawer({ title, subtitle, onClose, children, footer }: DrawerProps) {
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent side="right" aria-label={title}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {subtitle === undefined ? null : <SheetDescription>{subtitle}</SheetDescription>}
        </SheetHeader>

        <SheetBody>{children}</SheetBody>

        {footer === undefined ? null : <SheetFooter>{footer}</SheetFooter>}
      </SheetContent>
    </Sheet>
  );
}
